import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  HIGH_BUILD_SYSTEMS,
} from '@/components/proepoxy/productData';
import { toast } from 'sonner';
import { buildGuidedAreaItems, buildGuidedItems, itemsTotal } from '@/components/proepoxy/highbuild-flow/guidedItems';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildGuidedStep5({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit , voucher: voucherProp }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  const voucher = voucherProp || { id: null, value: 0 };
  const voucherAreaM2 = (highBuildAreas || []).reduce((a, x) => a + (parseFloat(x.m2) || 0), 0);
  const voucherOff = Math.min(voucher.value || 0, calculateHighBuildTotal());
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-[#27292E] mb-2">Order Summary</h2>
                  <p className="text-gray-600 mb-6">Review your selections before completing your order.</p>

                  {highBuildAreas.map((area) => {
                    const system = HIGH_BUILD_SYSTEMS[area.systemType];
                    if (!system) return null;
                    const areaItems = buildGuidedAreaItems(area);
                    const areaTotal = itemsTotal(areaItems);
                    return (
                      <div key={area.id} className="p-4 bg-gray-50 rounded-xl mb-4" data-testid={`hb-summary-area-${area.id}`}>
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                          <div>
                            <h3 className="font-bold text-[#27292E] text-lg">{area.name}</h3>
                            <p className="text-sm text-gray-500">{area.m2}m² • {system.name}{area.highBuildColor ? ` • ${area.highBuildColor}` : ''}</p>
                          </div>
                          <span className="text-lg font-bold text-[#7A0B7A]">£{areaTotal.toFixed(2)}</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          {areaItems.length === 0 && <p className="text-amber-700 text-xs">No products calculated for this area yet — go back and check the size and system.</p>}
                          {areaItems.map((it, i) => (
                            <div key={i} className="flex justify-between gap-3 text-gray-600" data-testid={`hb-summary-line-${area.id}-${i}`}>
                              <span>{it.product_name}{it.pack_label ? <span className="text-gray-400"> · {it.pack_label}</span> : null} × {it.qty}</span>
                              <span className="shrink-0">£{it.line_total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Grand Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getHighBuildItemCount()} item{getHighBuildItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                      {voucherOff > 0 && <p className="text-xs text-amber-300 font-semibold mt-1" data-testid="pe-voucher-line">Training voucher − £{voucherOff.toFixed(2)} · to pay £{(calculateHighBuildTotal() - voucherOff).toFixed(2)}</p>}
                    </div>
                    <p className="text-3xl font-bold">£{calculateHighBuildTotal().toFixed(2)}</p>
                  </div>
                </div>

                {/* Priority */}
                <PrioritySelector value={priority} onChange={setPriority} />

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button onClick={() => goToStep(4)} variant="outline" className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5">
                    <ArrowLeft className="w-5 h-5 mr-2" />Back
                  </Button>
                  <Button
                    onClick={() => {
                      const items = buildGuidedItems(highBuildAreas);
                      if (items.length === 0) { toast.error('No products on this order yet — check each area has a size and system.'); return; }
                      submitProEpoxyOrder({
                        system: 'high_build', mode: 'guided',
                        items, total: itemsTotal(items),
                        voucher_id: voucher.id || undefined, area_m2: voucherAreaM2,
                        priority,
                        selections: { highBuildAreas },
                      }, navigate);
                    }}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                    data-testid="proepoxy-complete-order-guided"
                  >
                    <Check className="w-5 h-5 mr-2" />Complete Order
                  </Button>
                </div>
              </div>
  );
}
